<script setup lang="ts">
import { ref } from 'vue'
import { healthService, type HealthCheck } from '@/api/health'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faCalendarDay,
  faStethoscope,
  faTemperatureHigh,
  faWeightHanging,
  faHeartPulse,
  faWind,
  faDna,
  faFileSignature,
  faNotesMedical,
  faUserMd,
  faChevronDown,
  faSave,
  faTimes,
  faCircleNotch,
  faCheckCircle,
  faCircleExclamation
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faCalendarDay,
  faStethoscope,
  faTemperatureHigh,
  faWeightHanging,
  faHeartPulse,
  faWind,
  faDna,
  faFileSignature,
  faNotesMedical,
  faUserMd,
  faChevronDown,
  faSave,
  faTimes,
  faCircleNotch,
  faCheckCircle,
  faCircleExclamation
)

interface Animal {
  id: string
  species: string
  breed?: string
}

const props = defineProps<{
  animal: Animal
  healthCheck?: HealthCheck | null
}>()

const emit = defineEmits<{
  success: []
}>()

const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const formData = ref({
  check_date: props.healthCheck?.check_date || new Date().toISOString().split('T')[0],
  check_type: props.healthCheck?.check_type || 'Routine',
  temperature: props.healthCheck?.temperature || undefined,
  weight: props.healthCheck?.weight || undefined,
  heart_rate: props.healthCheck?.heart_rate || undefined,
  respiratory_rate: props.healthCheck?.respiratory_rate || undefined,
  body_condition_score: props.healthCheck?.body_condition_score || undefined,
  findings: props.healthCheck?.findings || '',
  recommendations: props.healthCheck?.recommendations || '',
  veterinarian: props.healthCheck?.veterinarian || '',
  next_check_date: props.healthCheck?.next_check_date || ''
})

const checkTypeOptions = ['Routine', 'Emergency', 'Follow-up', 'Preventive']

async function submitForm() {
  loading.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const payload = {
      ...formData.value,
      animal: props.animal.id
    }

    await healthService.createHealthCheck(payload)
    successMessage.value = 'Health examination recorded successfully!'
    
    setTimeout(() => {
      emit('success')
    }, 1500)
  } catch (err: any) {
    errorMessage.value = err.response?.data?.detail || err.message || 'Failed to save health check'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <form @submit.prevent="submitForm" class="space-y-6">
    <!-- General Info Card -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-blue-500 rounded-full"></div>
          Examination Overview
        </h3>
      </div>
      
      <div class="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Check Date -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Check Date <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
              <FontAwesomeIcon icon="calendar-day" />
            </div>
            <input 
              type="date" 
              v-model="formData.check_date" 
              required
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Check Type -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Check Type <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
              <FontAwesomeIcon icon="stethoscope" />
            </div>
            <select 
              v-model="formData.check_type" 
              required
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all appearance-none cursor-pointer"
            >
              <option v-for="type in checkTypeOptions" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
            <div class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-300">
              <FontAwesomeIcon icon="chevron-down" class="text-xs" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vital Signs Card -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-emerald-500 rounded-full"></div>
          Vital Signs & Metrics
        </h3>
      </div>

      <div class="p-8 grid grid-cols-2 md:grid-cols-3 gap-8">
        <!-- Temperature -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Temp (°C)</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="temperature-high" />
            </div>
            <input 
              type="number" 
              step="0.1" 
              v-model="formData.temperature" 
              placeholder="0.0"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Weight -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Weight (kg)</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="weight-hanging" />
            </div>
            <input 
              type="number" 
              step="0.1" 
              v-model="formData.weight" 
              placeholder="0.0"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- BCS -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">B.C.S (1-9)</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="dna" />
            </div>
            <input 
              type="number" 
              min="1" 
              max="9" 
              v-model="formData.body_condition_score" 
              placeholder="5"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Heart Rate -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Heart Rate</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="heart-pulse" />
            </div>
            <input 
              type="number" 
              v-model="formData.heart_rate" 
              placeholder="BPM"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Resp Rate -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Resp. Rate</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="wind" />
            </div>
            <input 
              type="number" 
              v-model="formData.respiratory_rate" 
              placeholder="BRPM"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Clinical Notes Card -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-amber-500 rounded-full"></div>
          Clinical Observations
        </h3>
      </div>

      <div class="p-8 space-y-8">
        <!-- Findings -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Findings <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute top-4 left-4 text-slate-300 group-focus-within:text-amber-500 transition-colors">
              <FontAwesomeIcon icon="file-signature" />
            </div>
            <textarea 
              v-model="formData.findings" 
              required
              rows="3"
              placeholder="Describe the overall health and findings..."
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 text-slate-700 font-bold transition-all resize-none"
            ></textarea>
          </div>
        </div>

        <!-- Recommendations -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Recommendations</label>
          <div class="relative group">
            <div class="absolute top-4 left-4 text-slate-300 group-focus-within:text-amber-500 transition-colors">
              <FontAwesomeIcon icon="notes-medical" />
            </div>
            <textarea 
              v-model="formData.recommendations" 
              rows="2"
              placeholder="Next steps, treatment or dietary changes..."
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 text-slate-700 font-bold transition-all resize-none"
            ></textarea>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Veterinarian -->
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Veterinarian</label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-amber-500 transition-colors">
                <FontAwesomeIcon icon="user-md" />
              </div>
              <input 
                type="text" 
                v-model="formData.veterinarian" 
                placeholder="Doctor's name"
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 text-slate-700 font-bold transition-all"
              >
            </div>
          </div>

          <!-- Next Check Date -->
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Next Follow-up</label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-amber-500 transition-colors">
                <FontAwesomeIcon icon="calendar-day" />
              </div>
              <input 
                type="date" 
                v-model="formData.next_check_date" 
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 text-slate-700 font-bold transition-all"
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Feedback Messages -->
    <div class="space-y-3">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="successMessage" class="bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="check-circle" class="mr-3 text-lg" />
          {{ successMessage }}
        </div>
      </transition>
      
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="errorMessage" class="bg-rose-50 border border-rose-100 text-rose-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="circle-exclamation" class="mr-3 text-lg" />
          {{ errorMessage }}
        </div>
      </transition>
    </div>

    <!-- Form Actions -->
    <div class="flex justify-end gap-3 pt-4 border-t border-slate-100">
      <button 
        type="button" 
        @click="$emit('success')" 
        class="px-8 py-3.5 text-slate-400 font-black rounded-2xl hover:bg-slate-50 hover:text-slate-600 transition-all active:scale-95 flex items-center gap-2"
      >
        <FontAwesomeIcon icon="times" />
        Cancel
      </button>
      <button 
        type="submit" 
        :disabled="loading"
        class="px-10 py-3.5 bg-slate-900 text-white font-black rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-100 active:scale-95 disabled:bg-slate-200 disabled:shadow-none flex items-center gap-3"
      >
        <FontAwesomeIcon v-if="loading" icon="circle-notch" class="animate-spin" />
        <FontAwesomeIcon v-else icon="save" />
        {{ loading ? 'Saving...' : 'Record Examination' }}
      </button>
    </div>
  </form>
</template>
